import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { REPORT_ORDER_OPTIONS } from '../lib/constants';
import { projectAtom } from '../atoms/projectAtom';
import { cln } from '../lib/utils';
import { reportsPaginationAtom } from '../atoms';
import { useDebounce } from '../hooks';
import { PATHS } from '../lib/paths';
import { useReportsQuery, useStatusesQuery } from '../lib/queries';
import { ReportDetails } from '../components/ReportDetails';
import { DeleteReportConfirmDialog } from '../components/DeleteReportStatusConfirmDialog';
import { Select } from '../components/Select';
import { Input } from '../components/Input';

export default function ReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [project] = useAtom(projectAtom);
  const [pagination, setPagination] = useAtom(reportsPaginationAtom);
  // eslint-disable-next-line no-underscore-dangle
  const reportsQuery = useReportsQuery(
    { ...pagination, filters: { ...pagination.filters, pid: project?.id } },
    !!project,
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 700);

  const statusFilter = pagination.filters?.status?.$in;

  const setReportsPaginationStatusFilter = (value: string[]) => {
    setPagination({
      ...pagination,
      filters: {
        ...pagination.filters,
        status: {
          $in: value,
        },
      },
    });
  };

  const handleFilterClick = (filter: string) => () => {
    const relevantStatus = statusFilter?.some((status) => status === filter);
    if (relevantStatus)
      setReportsPaginationStatusFilter(statusFilter?.filter((f) => f !== filter) || []);
    else setReportsPaginationStatusFilter([...(statusFilter || []), filter]);
  };

  const statusQuery = useStatusesQuery(project?.id || '');
  const statuses = statusQuery.data?.statuses;

  useEffect(() => {
    setReportsPaginationStatusFilter(statuses ? statuses.map((status) => status.name) : []);
  }, [statuses]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: debouncedSearch,
      },
    }));
  }, [debouncedSearch, setPagination]);

  const orderDefaultValue = REPORT_ORDER_OPTIONS.findIndex(
    (option) => option.direction === pagination.sort?.createdAt,
  );

  return (
    <main className="grid gap-2 w-700 mx-auto">
      <div className="flex items-center">
        <h1 className="font-bold text-hero grow">{t('reports.title')}</h1>
        <button className="primary-fill !pl-2" onClick={() => navigate(PATHS.newReportPath())}>
          <i className="ri-add-line" />
          {t('report.title')}
        </button>
      </div>
      <h3 className="text-title font-semibold text-primary-500">{project?.name}</h3>

      <div className="flex gap-4 items-end w-full mt-10 mb-5">
        <Input
          label="common.search"
          placeholder="search"
          className="grow"
          type="search"
          value={search}
          onInput={(e: any) => {
            setSearch(e.target.value);
          }}
        />
      </div>
      <div className="flex mb-5">
        <div className="flex items-end gap-2">
          {statuses?.map((status) => (
            <button
              key={status.id}
              className={cln(
                'chip',
                statusFilter?.some((s) => status.name === s) && 'primary-fill border-transparent',
              )}
              onClick={handleFilterClick(status.name)}
            >
              {t(`report.status.${status.name}`)}
            </button>
          ))}
        </div>
        <Select
          label="order.label"
          className="justify-end"
          defaultValue={orderDefaultValue}
          onChange={(e: any) => {
            const { direction, field } = REPORT_ORDER_OPTIONS[e.target.value];
            setPagination((p) => ({ ...p, sort: { ...p.sort, [field]: direction } }));
          }}
        >
          {REPORT_ORDER_OPTIONS.map((opt, index) => (
            <option key={opt.label} value={index}>
              {t(opt.label)}
            </option>
          ))}
        </Select>
      </div>
      {reportsQuery.data?.reports.map((report) => (
        <ReportDetails key={report.id} report={report} />
      ))}
      <DeleteReportConfirmDialog />
    </main>
  );
}
